import { Button } from "@dropbox/dig-components/buttons";
import { Modal } from "@dropbox/dig-components/modal";
import { Text } from "@dropbox/dig-components/typography";
import i18n from "@mirage/translations";

import { useEffect, useState, type ReactElement } from "react";
import { SearchResult } from "../search/search-result";

import { TextArea } from "@dropbox/dig-components/text_fields";
import { Stack } from "@dropbox/dig-foundations";
import { RecentPromptsModule } from "@mirage/mosaics/Results/RecentPromptsModule";
import { useRecentPrompts } from "@mirage/mosaics/Results/useRecentPrompts";
import { getDefaultPromptId } from "@mirage/service-compute-relevance";
import { RelevanceRubric } from "@mirage/service-compute-relevance/service/types";
import { showSnackbar } from "@mirage/shared/snackbar";
import type { Rating } from "../relevance/rating";
import RelevanceRating from "../relevance/rating";
import { Recommendation } from "../search/recommendation";
import styles from "./ComputeRelevanceModal.module.css";

export type RelevanceResult = {
  rating: Rating | null;
  explanation: string;
  sentPrompt: string;
  userMessageContent: string;
  promptId?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  query: string;
  item: SearchResult | Recommendation;
  result: Partial<RelevanceResult>;
  prompt: string | null;
  resetExplanation: () => void;
  setPrompt: (prompt: string) => void;
  rubric: RelevanceRubric;
  userMessageContent: string | null;
  submitPrompt: () => void;
  loading: boolean;
  error?: Error | null;
  refetchRecentPrompts?: () => void;
};

export const ComputeRelevanceModal = ({
  open,
  onClose,
  item,
  query,
  result,
  prompt,
  resetExplanation,
  rubric,
  setPrompt,
  userMessageContent,
  submitPrompt,
  loading,
  error,
}: Props): ReactElement => {
  const { recentPrompts, refetchRecentPrompts, setDefaultPrompt } =
    useRecentPrompts(rubric);
  const [userDefaultPromptId, setUserDefaultPromptId] = useState<string | null>(
    null,
  );

  // Load the current default prompt ID when the component mounts
  useEffect(() => {
    if (open) {
      getDefaultPromptId(rubric)
        .then((val) => {
          setUserDefaultPromptId(val);
        })
        .catch(() => {
          // Ignore error, just keep it as null
          setUserDefaultPromptId(null);
        });
    }
  }, [getDefaultPromptId, open]);

  const handleSubmitPrompt = () => {
    submitPrompt();
    refetchRecentPrompts();
  };

  const handleSetAsDefault = async () => {
    const currentPrompt = recentPrompts.find((p) => p.prompt === prompt);
    if (!currentPrompt) return;

    try {
      await setDefaultPrompt(currentPrompt.uuid);
      setUserDefaultPromptId(currentPrompt.uuid);
      showSnackbar({
        title: i18n.t("default_prompt_set"),
      });
    } catch (error) {
      showSnackbar({
        title: i18n.t("error"),
      });
    }
  };

  const handleClearDefault = async () => {
    try {
      await setDefaultPrompt(null);
      setUserDefaultPromptId(null);
      showSnackbar({
        title: i18n.t("default_prompt_cleared"),
      });
    } catch (error) {
      showSnackbar({
        title: i18n.t("error"),
      });
    }
  };

  // Check if current prompt is the user's default
  const currentPrompt = recentPrompts.find((p) => p.prompt === prompt);
  const isCurrentPromptUserDefault =
    currentPrompt && userDefaultPromptId === currentPrompt.uuid;

  return (
    <Modal
      open={open}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={true}
      withCloseButton={i18n.t("close_modal_aria")}
      isCentered
      width={1200}
    >
      <Modal.Header>
        <Modal.Title>{i18n.t("search_relevance_modal_title")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.modalColumns}>
          <div className={styles.recentPromptsColumn}>
            <RecentPromptsModule
              rubric={rubric}
              onSelectPrompt={(newPrompt) => {
                setPrompt(newPrompt);
                resetExplanation();
              }}
            />
          </div>
          <Stack
            dir="vertical"
            gap="Macro XSmall"
            className={styles.inputColumn}
          >
            {error && (
              <div>
                <Text className={styles.error}>
                  {i18n.t("search_relevance_modal_error")}
                </Text>
              </div>
            )}
            <div>
              <Text>{i18n.t("search_relevance_modal_description")}</Text>
            </div>
            <div>
              <Text>
                <strong>Search Result:</strong> {item.title}
              </Text>
            </div>
            <div>
              <Text>
                <strong>Search Query:</strong> {query}
              </Text>
            </div>
            {result?.rating && (
              <div>
                <Text>
                  <strong>Relevance Rating:</strong>{" "}
                  <RelevanceRating rating={result?.rating} />
                </Text>
              </div>
            )}
            {result?.explanation && (
              <div>
                <Text>
                  <strong>Relevance Explanation:</strong> {result?.explanation}
                </Text>
              </div>
            )}
            <div>
              <Text>
                <label htmlFor="SearchPrompt">
                  <strong>Utilized Prompt</strong>
                  <div>
                    <TextArea
                      rows={4}
                      disabled={loading}
                      id="SearchPrompt"
                      data-testid="SearchPrompt"
                      resizable="vertical"
                      value={prompt ?? ""}
                      onChange={(e) => setPrompt(e.currentTarget.value)}
                    />
                  </div>
                </label>
              </Text>
            </div>
            <div>
              <Text>
                <label htmlFor="UserMessageContent">
                  <strong>User Message Content</strong>
                  <div>
                    <TextArea
                      rows={4}
                      id="UserMessageContent"
                      readOnly
                      disabled
                      resizable="vertical"
                      value={userMessageContent || ""}
                    />
                  </div>
                </label>
              </Text>
            </div>
          </Stack>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div>
          {currentPrompt && isCurrentPromptUserDefault ? (
            <Button variant="transparent" onClick={handleClearDefault}>
              {i18n.t("clear_default_prompt")}
            </Button>
          ) : currentPrompt ? (
            <Button variant="transparent" onClick={handleSetAsDefault}>
              {i18n.t("set_as_default_prompt")}
            </Button>
          ) : null}
          <Button variant="opacity" onClick={onClose}>
            {i18n.t("close")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmitPrompt}
            disabled={loading}
            isLoading={loading}
          >
            {i18n.t("submit_prompt")}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
